import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  adminGetBookings,
  adminUpdateBooking,
  adminGetConsultants,
  adminSaveConsultant,
  adminDeleteConsultant,
  adminGetConsultationServices,
  adminSaveConsultationService,
  adminDeleteConsultationService,
} from "../../api/consultationApi";
import { formatINR, formatSlotDateTime, resolveImage } from "../Consultation/consultationUtils";
import {
  contentFromForm,
  emptyBlock,
  emptyStep,
  formFromContent,
  isPlainObject,
  validateServiceForm,
} from "./serviceContentForm";
import "./AdminConsultations.css";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const STATUSES = ["pending_payment", "paid", "completed", "cancelled"];
const CATEGORY_LABEL = { astrology: "Astrology", numerology: "Numerology", vastu: "Vastu" };

const emptyConsultant = () => ({
  id: null,
  name: "",
  headline: "",
  bio: "",
  experienceYears: 0,
  specialisations: "",
  languages: "",
  defaultFee: "",
  durationMinutes: 15,
  isActive: true,
  sortOrder: 0,
  photoUrl: "",
  // weekday -> { on, startTime, endTime } (one window per day keeps the UI simple)
  schedule: WEEKDAYS.map((_, weekday) => ({ weekday, on: weekday >= 1 && weekday <= 6, startTime: "10:00", endTime: "18:00" })),
  // serviceId -> { on, fee }
  links: {},
});

const fromApi = (c) => {
  const schedule = WEEKDAYS.map((_, weekday) => {
    const row = (c.availability || []).find((a) => a.weekday === weekday);
    return { weekday, on: Boolean(row), startTime: row?.startTime || "10:00", endTime: row?.endTime || "18:00" };
  });
  const links = {};
  (c.services || []).forEach((l) => {
    links[l.serviceId] = { on: true, fee: l.fee == null ? "" : String(Number(l.fee)) };
  });
  return {
    id: c.id,
    name: c.name,
    headline: c.headline || "",
    bio: c.bio || "",
    experienceYears: c.experienceYears,
    specialisations: (c.specialisations || []).join(", "),
    languages: (c.languages || []).join(", "),
    defaultFee: String(Number(c.defaultFee)),
    durationMinutes: c.durationMinutes,
    isActive: c.isActive,
    sortOrder: c.sortOrder,
    photoUrl: c.photoUrl || "",
    schedule,
    links,
  };
};

export default function AdminConsultations({ token }) {
  const [tab, setTab] = useState("bookings");
  const [notice, setNotice] = useState(null);

  const flash = (type, text) => {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), 4000);
  };

  return (
    <div className="panel-tab ac-wrap">
      {notice && <div className={`feedback-alert ${notice.type}`}>{notice.text}</div>}

      <div className="ac-subtabs">
        {[
          ["bookings", "Bookings"],
          ["consultants", "Consultants"],
          ["services", "Services"],
        ].map(([key, label]) => (
          <button key={key} className={`ac-subtab ${tab === key ? "is-active" : ""}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === "bookings" && <Bookings token={token} flash={flash} />}
      {tab === "consultants" && <Consultants token={token} flash={flash} />}
      {tab === "services" && <Services token={token} flash={flash} />}
    </div>
  );
}

/* ------------------------------------------------------------- bookings */

function Bookings({ token, flash }) {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await adminGetBookings({ token, page, status, search });
    if (result.ok && result.success) {
      setRows(result.data.bookings);
      setPagination(result.data.pagination);
    } else {
      flash("error", result.message || "Unable to load bookings");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, status, search]);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id, payload) => {
    const result = await adminUpdateBooking(id, payload, token);
    if (result.ok && result.success) {
      flash("success", "Booking updated");
      load();
    } else {
      flash("error", result.message || "Update failed");
    }
  };

  return (
    <>
      <div className="panel-filters ac-filters">
        <input
          className="ac-input"
          placeholder="Search ref, name, email, phone..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="ac-input"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace("_", " ")}
            </option>
          ))}
        </select>
      </div>

      <div className="table-container">
        <table className="dashboard-table main-table">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Customer</th>
              <th>Consultation</th>
              <th>Slot (IST)</th>
              <th>Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="6">No bookings found.</td></tr>
            ) : (
              rows.map((b) => (
                <tr key={b.id}>
                  <td>
                    <strong>{b.ref}</strong>
                    <div className="user-email">{new Date(b.createdAt).toLocaleDateString()}</div>
                  </td>
                  <td>
                    <div className="user-name">{b.customerName}</div>
                    <div className="user-email">{b.customerPhone}</div>
                    <div className="user-email">{b.customerEmail}</div>
                    {b.dateOfBirth && (
                      <div className="user-email">
                        {b.dateOfBirth} {b.timeOfBirth} · {b.placeOfBirth}
                      </div>
                    )}
                    {(b.concerns?.length > 0 || b.concernNote) && (
                      <div className="user-email">{[...(b.concerns || []), b.concernNote].filter(Boolean).join(" · ")}</div>
                    )}
                  </td>
                  <td>
                    <div className="user-name">{b.service?.title}</div>
                    <div className="user-email">{b.consultant?.name}</div>
                  </td>
                  <td>{formatSlotDateTime(b.slotStart)}</td>
                  <td>{formatINR(b.amount)}</td>
                  <td>
                    <select
                      className={`status-select ${b.status}`}
                      value={b.status}
                      onChange={(e) => update(b.id, { status: e.target.value })}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s.replace("_", " ")}
                        </option>
                      ))}
                    </select>
                    {b.adminNote && <div className="ac-warn">{b.adminNote}</div>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.pages > 1 && (
        <div className="pagination-controls">
          <button disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {pagination.page} of {pagination.pages}</span>
          <button disabled={page >= pagination.pages || loading} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </>
  );
}

/* ----------------------------------------------------------- consultants */

function Consultants({ token, flash }) {
  const [list, setList] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [c, s] = await Promise.all([adminGetConsultants(token), adminGetConsultationServices(token)]);
    if (c.ok && c.success) setList(c.data);
    else flash("error", c.message || "Unable to load consultants");
    if (s.ok && s.success) setServices(s.data);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const setDay = (weekday, patch) =>
    setForm((f) => ({ ...f, schedule: f.schedule.map((d) => (d.weekday === weekday ? { ...d, ...patch } : d)) }));

  const setLink = (serviceId, patch) =>
    setForm((f) => ({ ...f, links: { ...f.links, [serviceId]: { on: false, fee: "", ...f.links[serviceId], ...patch } } }));

  const open = (consultant) => {
    setPhoto(null);
    setForm(consultant ? fromApi(consultant) : emptyConsultant());
  };

  const save = async (e) => {
    e.preventDefault();
    const availability = form.schedule
      .filter((d) => d.on)
      .map(({ weekday, startTime, endTime }) => ({ weekday, startTime, endTime }));
    const links = Object.entries(form.links)
      .filter(([, v]) => v.on)
      .map(([serviceId, v]) => ({ serviceId, fee: v.fee === "" ? null : Number(v.fee) }));

    const data = new FormData();
    ["name", "headline", "bio", "experienceYears", "specialisations", "languages", "defaultFee", "durationMinutes", "sortOrder"].forEach((k) =>
      data.append(k, form[k])
    );
    data.append("isActive", String(form.isActive));
    data.append("availability", JSON.stringify(availability));
    data.append("services", JSON.stringify(links));
    if (photo) data.append("photo", photo);

    setSaving(true);
    const result = await adminSaveConsultant(form.id, data, token);
    setSaving(false);

    if (result.ok && result.success) {
      flash("success", form.id ? "Consultant updated" : "Consultant added");
      setForm(null);
      load();
    } else {
      flash("error", result.message || "Unable to save consultant");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}? Consultants with bookings are deactivated instead.`)) return;
    const result = await adminDeleteConsultant(c.id, token);
    if (result.ok && result.success) {
      flash("success", result.message);
      load();
    } else {
      flash("error", result.message || "Unable to remove consultant");
    }
  };

  if (form) {
    const grouped = ["astrology", "numerology", "vastu"].map((cat) => [cat, services.filter((s) => s.category === cat)]);
    return (
      <form className="ac-form" onSubmit={save}>
        <h3>{form.id ? "Edit consultant" : "Add consultant"}</h3>

        <div className="ac-grid">
          <label>Name *<input className="ac-input" required value={form.name} onChange={(e) => set("name", e.target.value)} /></label>
          <label>Headline<input className="ac-input" value={form.headline} onChange={(e) => set("headline", e.target.value)} placeholder="Vedic Astrologer" /></label>
          <label>Experience (years)<input className="ac-input" type="number" min="0" value={form.experienceYears} onChange={(e) => set("experienceYears", e.target.value)} /></label>
          <label>Default fee (₹) *<input className="ac-input" type="number" min="0" required value={form.defaultFee} onChange={(e) => set("defaultFee", e.target.value)} /></label>
          <label>Consultation length (min)<input className="ac-input" type="number" min="5" max="240" value={form.durationMinutes} onChange={(e) => set("durationMinutes", e.target.value)} /></label>
          <label>Display order<input className="ac-input" type="number" value={form.sortOrder} onChange={(e) => set("sortOrder", e.target.value)} /></label>
          <label>Specialisations (comma separated)<input className="ac-input" value={form.specialisations} onChange={(e) => set("specialisations", e.target.value)} /></label>
          <label>Languages (comma separated)<input className="ac-input" value={form.languages} onChange={(e) => set("languages", e.target.value)} /></label>
        </div>

        <label>About the consultant<textarea className="ac-input" rows="4" value={form.bio} onChange={(e) => set("bio", e.target.value)} /></label>

        <div className="ac-photo">
          {(photo || form.photoUrl) && (
            <img src={photo ? URL.createObjectURL(photo) : resolveImage(form.photoUrl)} alt="Consultant" />
          )}
          <label>Photo<input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setPhoto(e.target.files[0] || null)} /></label>
        </div>

        <label className="ac-check"><input type="checkbox" checked={form.isActive} onChange={(e) => set("isActive", e.target.checked)} /> Visible on the website</label>

        <h4>Weekly availability (IST)</h4>
        <div className="ac-schedule">
          {form.schedule.map((d) => (
            <div className="ac-day" key={d.weekday}>
              <label className="ac-check">
                <input type="checkbox" checked={d.on} onChange={(e) => setDay(d.weekday, { on: e.target.checked })} /> {WEEKDAYS[d.weekday]}
              </label>
              <input className="ac-input" type="time" disabled={!d.on} value={d.startTime} onChange={(e) => setDay(d.weekday, { startTime: e.target.value })} />
              <span>to</span>
              <input className="ac-input" type="time" disabled={!d.on} value={d.endTime} onChange={(e) => setDay(d.weekday, { endTime: e.target.value })} />
            </div>
          ))}
        </div>

        <h4>Services offered (leave fee blank to use the default fee)</h4>
        {grouped.map(([cat, items]) => (
          <div key={cat} className="ac-services">
            <strong>{CATEGORY_LABEL[cat]}</strong>
            {items.map((s) => {
              const link = form.links[s.id] || { on: false, fee: "" };
              return (
                <div className="ac-service-row" key={s.id}>
                  <label className="ac-check">
                    <input type="checkbox" checked={link.on} onChange={(e) => setLink(s.id, { on: e.target.checked })} /> {s.title}
                  </label>
                  <input className="ac-input ac-fee" type="number" min="0" placeholder="Fee ₹" disabled={!link.on} value={link.fee} onChange={(e) => setLink(s.id, { fee: e.target.value })} />
                </div>
              );
            })}
          </div>
        ))}

        <div className="ac-actions">
          <button type="button" className="ac-btn" onClick={() => setForm(null)}>Cancel</button>
          <button type="submit" className="ac-btn ac-btn-primary" disabled={saving}>{saving ? "Saving..." : "Save consultant"}</button>
        </div>
      </form>
    );
  }

  return (
    <>
      <div className="ac-bar">
        <button className="ac-btn ac-btn-primary" onClick={() => open(null)}>+ Add consultant</button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : list.length === 0 ? (
        <p>No consultants yet. Add one so customers can book.</p>
      ) : (
        <div className="ac-cards">
          {list.map((c) => (
            <div className={`ac-card ${c.isActive ? "" : "is-off"}`} key={c.id}>
              <div className="ac-card-head">
                {c.photoUrl ? <img src={resolveImage(c.photoUrl)} alt="" /> : <span className="ac-avatar">{c.name[0]}</span>}
                <div>
                  <strong>{c.name}</strong>
                  <div className="user-email">{c.headline}</div>
                </div>
              </div>
              <div className="user-email">
                {c.experienceYears} yrs · {formatINR(c.defaultFee)} · {c.durationMinutes} min · {c.services.length} services
              </div>
              {!c.isActive && <div className="ac-warn">Hidden from website</div>}
              <div className="ac-actions">
                <button className="ac-btn" onClick={() => open(c)}>Edit</button>
                <button className="ac-btn ac-btn-danger" onClick={() => remove(c)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* -------------------------------------------------------------- services */

const CATEGORIES = ["astrology", "numerology", "vastu"];

const emptyService = () => ({
  id: null,
  category: "astrology",
  title: "",
  tagline: "",
  shortDescription: "",
  sortOrder: "",
  isActive: true,
  imageUrl: "",
});

function Services({ token, flash }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [base, setBase] = useState({}); // the content as loaded; keys the form does not know are kept
  const [form, setForm] = useState(null); // the page-content form fields
  const [mode, setMode] = useState("form"); // "form" | "json"
  const [jsonText, setJsonText] = useState("");
  const [image, setImage] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await adminGetConsultationServices(token);
    if (result.ok && result.success) setList(result.data);
    else flash("error", result.message || "Unable to load services");
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const preview = useMemo(() => (image ? URL.createObjectURL(image) : ""), [image]);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const startEdit = (service) => {
    const content = isPlainObject(service?.content) ? service.content : {};
    setEditing(service ? { ...service, sortOrder: String(service.sortOrder) } : emptyService());
    setBase(content);
    setForm(formFromContent(content));
    setJsonText(JSON.stringify(content, null, 2));
    // A page with its own `sections` layout (e.g. Mobile Number Analysis) is easiest to edit as JSON.
    setMode(Array.isArray(content.sections) ? "json" : "form");
    setImage(null);
    setRemoveImage(false);
  };

  const parseJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      return isPlainObject(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };

  const switchMode = (next) => {
    if (next === mode) return;
    if (next === "json") {
      setJsonText(JSON.stringify(contentFromForm(base, form), null, 2));
    } else {
      const parsed = parseJson();
      if (!parsed) {
        flash("error", "Page content is not a valid JSON object, fix it before switching to the form");
        return;
      }
      setBase(parsed);
      setForm(formFromContent(parsed));
    }
    setMode(next);
  };

  const set = (name, value) => setEditing((s) => ({ ...s, [name]: value }));
  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const patchItem = (key, i, patch) =>
    setForm((f) => ({ ...f, [key]: f[key].map((item, idx) => (idx === i ? { ...item, ...patch } : item)) }));
  const addItem = (key, blank) => setForm((f) => ({ ...f, [key]: [...f[key], blank] }));
  const removeItem = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    if (!editing.title.trim()) {
      flash("error", "Please enter a title");
      return;
    }
    let content;
    if (mode === "json") {
      content = parseJson();
      if (!content) {
        flash("error", "Page content is not a valid JSON object");
        return;
      }
    } else {
      const problem = validateServiceForm(form);
      if (problem) {
        flash("error", problem);
        return;
      }
      content = contentFromForm(base, form);
    }

    const data = new FormData();
    data.append("category", editing.category);
    data.append("title", editing.title.trim());
    data.append("tagline", editing.tagline || "");
    data.append("shortDescription", editing.shortDescription || "");
    if (String(editing.sortOrder).trim() !== "") data.append("sortOrder", editing.sortOrder);
    data.append("isActive", String(editing.isActive));
    data.append("content", JSON.stringify(content));
    if (image) data.append("image", image);
    else if (removeImage) data.append("removeImage", "true");

    setSaving(true);
    const result = await adminSaveConsultationService(editing.id, data, token);
    setSaving(false);
    if (result.ok && result.success) {
      flash("success", editing.id ? "Service updated" : "Service added");
      setEditing(null);
      load();
    } else {
      flash("error", result.message || "Unable to save service");
    }
  };

  const remove = async (service) => {
    if (!window.confirm(`Remove "${service.title}"? A service that already has bookings is hidden instead of deleted.`)) return;
    const result = await adminDeleteConsultationService(service.id, token);
    if (result.ok && result.success) {
      flash("success", result.message);
      load();
    } else {
      flash("error", result.message || "Unable to remove service");
    }
  };

  if (editing) {
    const shownImage = preview || (!removeImage && editing.imageUrl ? resolveImage(editing.imageUrl) : "");
    return (
      <form className="ac-form" onSubmit={save}>
        <h3>{editing.id ? "Edit service" : "Add service"}</h3>
        <div className="ac-grid">
          <label>
            Category
            <select className="ac-input" value={editing.category} onChange={(e) => set("category", e.target.value)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABEL[c]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Title
            <input className="ac-input" required value={editing.title} onChange={(e) => set("title", e.target.value)} />
          </label>
        </div>
        <label>Tagline<input className="ac-input" value={editing.tagline || ""} onChange={(e) => set("tagline", e.target.value)} /></label>
        <label>Short description (shown on cards)<textarea className="ac-input" rows="3" value={editing.shortDescription || ""} onChange={(e) => set("shortDescription", e.target.value)} /></label>
        <div className="ac-grid">
          <label>Display order (blank = last in its category)<input className="ac-input" type="number" value={editing.sortOrder} onChange={(e) => set("sortOrder", e.target.value)} /></label>
          <label className="ac-check"><input type="checkbox" checked={editing.isActive} onChange={(e) => set("isActive", e.target.checked)} /> Visible on the website</label>
        </div>

        <div className="ac-photo">
          {shownImage && <img className="ac-thumb" src={shownImage} alt="Service" />}
          <label>
            Image (shown on the service card and page)
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => {
                setImage(e.target.files[0] || null);
                setRemoveImage(false);
              }}
            />
          </label>
          {(editing.imageUrl || image) && !removeImage && (
            <button
              type="button"
              className="ac-btn"
              onClick={() => {
                setImage(null);
                setRemoveImage(true);
              }}
            >
              Remove image
            </button>
          )}
        </div>

        <div className="ac-modes" role="tablist" aria-label="Page content editor">
          <strong>Page content</strong>
          {[
            ["form", "Form"],
            ["json", "Advanced (JSON)"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={mode === key}
              className={`ac-subtab ${mode === key ? "is-active" : ""}`}
              onClick={() => switchMode(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === "json" ? (
          <label>
            Page content (JSON object). Pages with a "sections" list use their own layout.
            <textarea className="ac-input ac-code" rows="18" value={jsonText} onChange={(e) => setJsonText(e.target.value)} spellCheck="false" />
          </label>
        ) : (
          <>
            <label>
              Introduction (separate paragraphs with a blank line)
              <textarea className="ac-input" rows="6" value={form.intro} onChange={(e) => setField("intro", e.target.value)} />
            </label>

            <h4>"What will we provide you?" blocks</h4>
            {form.provide.map((b, i) => (
              <div className="ac-block" key={i}>
                <label>Title<input className="ac-input" value={b.title} onChange={(e) => patchItem("provide", i, { title: e.target.value })} /></label>
                <label>Description<textarea className="ac-input" rows="2" value={b.description} onChange={(e) => patchItem("provide", i, { description: e.target.value })} /></label>
                <label>Points (one per line)<textarea className="ac-input" rows="4" value={b.points} onChange={(e) => patchItem("provide", i, { points: e.target.value })} /></label>
                <label>Note (optional)<input className="ac-input" value={b.note} onChange={(e) => patchItem("provide", i, { note: e.target.value })} /></label>
                <button type="button" className="ac-btn ac-btn-danger" onClick={() => removeItem("provide", i)}>Remove block</button>
              </div>
            ))}
            <div><button type="button" className="ac-btn" onClick={() => addItem("provide", emptyBlock())}>+ Add block</button></div>
            <label>Highlighted note under the blocks (optional)<textarea className="ac-input" rows="2" value={form.provideNote} onChange={(e) => setField("provideNote", e.target.value)} /></label>

            <h4>"How will we analyse" steps</h4>
            {form.analysisSteps.map((s, i) => (
              <div className="ac-block" key={i}>
                <label>Title<input className="ac-input" value={s.title} onChange={(e) => patchItem("analysisSteps", i, { title: e.target.value })} /></label>
                <label>Description<textarea className="ac-input" rows="3" value={s.description} onChange={(e) => patchItem("analysisSteps", i, { description: e.target.value })} /></label>
                <button type="button" className="ac-btn ac-btn-danger" onClick={() => removeItem("analysisSteps", i)}>Remove step</button>
              </div>
            ))}
            <div><button type="button" className="ac-btn" onClick={() => addItem("analysisSteps", emptyStep())}>+ Add step</button></div>
            <label>Concern options shown under the steps (one per line)<textarea className="ac-input" rows="4" value={form.concernOptions} onChange={(e) => setField("concernOptions", e.target.value)} /></label>

            <label>What the customer receives (one per line)<textarea className="ac-input" rows="6" value={form.receive} onChange={(e) => setField("receive", e.target.value)} /></label>
            <div className="ac-grid">
              <label>Closing heading<input className="ac-input" value={form.closingHeading} onChange={(e) => setField("closingHeading", e.target.value)} /></label>
              <label>Closing text<textarea className="ac-input" rows="3" value={form.closingText} onChange={(e) => setField("closingText", e.target.value)} /></label>
            </div>
          </>
        )}

        <div className="ac-actions">
          <button type="button" className="ac-btn" onClick={() => setEditing(null)}>Cancel</button>
          <button type="submit" className="ac-btn ac-btn-primary" disabled={saving}>{saving ? "Saving..." : editing.id ? "Save service" : "Add service"}</button>
        </div>
      </form>
    );
  }

  return (
    <>
      <div className="ac-bar">
        <button className="ac-btn ac-btn-primary" onClick={() => startEdit(null)}>+ Add service</button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="table-container">
          <table className="dashboard-table main-table">
            <thead>
              <tr><th>Category</th><th>Service</th><th>Image</th><th>Visible</th><th /></tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id}>
                  <td>{CATEGORY_LABEL[s.category]}</td>
                  <td>{s.title}</td>
                  <td>{s.imageUrl ? <img className="ac-thumb ac-thumb-sm" src={resolveImage(s.imageUrl)} alt="" /> : "-"}</td>
                  <td>{s.isActive ? "Yes" : "No"}</td>
                  <td>
                    <div className="ac-actions">
                      <button className="ac-btn" onClick={() => startEdit(s)}>Edit</button>
                      <button className="ac-btn ac-btn-danger" onClick={() => remove(s)}>Remove</button>
                    </div>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr><td colSpan="5">No services yet. Add one so customers can book.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
